// Result charts — pure model layer (no DOM). Turns a query result set
// (columns + rows) + a chart spec into a renderable model that Chart.jsx
// projects to SVG. Kept dependency-free and node-testable (same ethos as
// lib/qbsql.js / lib/erlayout.js). Reused by both the query result "Chart" view
// and (later) the report builder.
//
// SPEC shape:
//   { type:'bar'|'line'|'area'|'pie', x:colName, y:[colName,...], series:colName|'' }
//
// MODEL shape (buildChartModel output):
//   { type, categories:[label,...],
//     series:[{ name, color, values:[number,...] }],   // values aligned to categories
//     yMin, yMax, yTicks:[number,...], total, warnings:[] }

export const CHART_TYPES = [
  { v: 'bar', label: 'Bar' },
  { v: 'line', label: 'Line' },
  { v: 'area', label: 'Area' },
  { v: 'pie', label: 'Pie' },
];

// A categorical palette that reads well on both light and dark themes.
export const PALETTE = [
  '#0ea5e9', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6',
  '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#84cc16',
  '#06b6d4', '#e11d48',
];

const colorAt = (i) => PALETTE[i % PALETTE.length];

// Coerce a cell to a number for plotting. Strips thousands separators and
// currency-ish prefixes; non-numeric / null → NaN (callers decide how to treat).
export function toNum(v) {
  if (v == null) return NaN;
  if (typeof v === 'number') return v;
  const s = String(v).trim().replace(/[, ]/g, '');
  if (s === '') return NaN;
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
}

// Sample up to 40 rows of a column to decide if it's numeric (≥80% parseable).
export function inferColumnKinds(columns, rows) {
  const names = (columns || []).map((c) => (typeof c === 'string' ? c : c.name));
  return names.map((name, ci) => {
    let seen = 0, num = 0;
    for (let i = 0; i < rows.length && seen < 40; i++) {
      const v = rows[i]?.[ci];
      if (v == null || v === '') continue;
      seen++;
      if (Number.isFinite(toNum(v))) num++;
    }
    return { name, index: ci, numeric: seen > 0 && num / seen >= 0.8 };
  });
}

// A sensible default spec: first non-numeric column → x (categories), first
// numeric column → the single measure. Falls back to first/second columns.
export function defaultSpec(columns, rows) {
  const kinds = inferColumnKinds(columns, rows);
  if (kinds.length === 0) return { type: 'bar', x: '', y: [], series: '' };
  const firstCat = kinds.find((k) => !k.numeric) || kinds[0];
  const firstNum = kinds.find((k) => k.numeric && k.name !== firstCat.name)
    || kinds.find((k) => k.name !== firstCat.name)
    || kinds[0];
  return { type: 'bar', x: firstCat.name, y: firstNum ? [firstNum.name] : [], series: '' };
}

// "Nice" axis ticks spanning [min,max] (~count intervals), rounded to 1/2/5×10^k.
export function niceTicks(min, max, count = 5) {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [0, 1];
  if (min === max) { if (min === 0) return [0, 1]; min = Math.min(0, min); }
  const range = max - min || 1;
  const rawStep = range / Math.max(1, count);
  const mag = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const norm = rawStep / mag;
  const step = (norm >= 5 ? 5 : norm >= 2 ? 2 : 1) * mag;
  const start = Math.floor(min / step) * step;
  const end = Math.ceil(max / step) * step;
  const ticks = [];
  // Guard against fp drift producing a runaway loop.
  for (let v = start, i = 0; v <= end + step * 0.5 && i < 1000; v += step, i++) {
    ticks.push(Number(v.toFixed(10)));
  }
  return ticks;
}

const indexOfCol = (columns, name) => {
  const names = (columns || []).map((c) => (typeof c === 'string' ? c : c.name));
  return names.indexOf(name);
};

// Build the renderable chart model from data + spec. Duplicate x categories are
// aggregated by SUM (so un-grouped result sets still chart sensibly).
export function buildChartModel(columns, rows, spec) {
  const warnings = [];
  const type = spec?.type || 'bar';
  rows = rows || [];

  const xi = indexOfCol(columns, spec?.x);
  if (xi < 0) return { type, categories: [], series: [], yMin: 0, yMax: 1, yTicks: [0, 1], total: 0, warnings: ['Pick an X (category) column.'] };

  // Categories in first-seen order.
  const categories = [];
  const catIndex = new Map();
  for (const r of rows) {
    const key = r[xi] == null ? '∅' : String(r[xi]);
    if (!catIndex.has(key)) { catIndex.set(key, categories.length); categories.push(key); }
  }

  // ── Pie: single measure summed per category ────────────────────────────────
  if (type === 'pie') {
    const yName = (spec.y || [])[0];
    const yIdx = indexOfCol(columns, yName);
    if (yIdx < 0) return { type, categories, series: [], yMin: 0, yMax: 1, yTicks: [], total: 0, warnings: ['Pick a Y (value) column.'] };
    const values = new Array(categories.length).fill(0);
    for (const r of rows) {
      const ci = catIndex.get(r[xi] == null ? '∅' : String(r[xi]));
      const n = toNum(r[yIdx]);
      if (Number.isFinite(n)) values[ci] += n;
    }
    const total = values.reduce((a, b) => a + b, 0);
    const series = [{ name: yName, color: PALETTE[0], values }];
    return { type, categories, series, yMin: 0, yMax: Math.max(...values, 0), yTicks: [], total, warnings };
  }

  // ── Bar/line/area ───────────────────────────────────────────────────────────
  let series = [];
  const seriesCol = spec.series;
  const si = seriesCol ? indexOfCol(columns, seriesCol) : -1;

  if (si >= 0) {
    // Pivot a single measure by the series column.
    const yName = (spec.y || [])[0];
    const yIdx = indexOfCol(columns, yName);
    if (yIdx < 0) { warnings.push('Pick a Y (value) column.'); }
    const names = [];
    const nameIndex = new Map();
    const grid = []; // grid[seriesIdx][catIdx]
    for (const r of rows) {
      const sKey = r[si] == null ? '∅' : String(r[si]);
      if (!nameIndex.has(sKey)) { nameIndex.set(sKey, names.length); names.push(sKey); grid.push(new Array(categories.length).fill(0)); }
      const seriesIdx = nameIndex.get(sKey);
      const ci = catIndex.get(r[xi] == null ? '∅' : String(r[xi]));
      const n = yIdx >= 0 ? toNum(r[yIdx]) : NaN;
      if (Number.isFinite(n)) grid[seriesIdx][ci] += n;
    }
    series = names.map((name, i) => ({ name, color: colorAt(i), values: grid[i] }));
  } else {
    // One series per chosen Y column.
    const yCols = (spec.y || []).map((name) => ({ name, idx: indexOfCol(columns, name) })).filter((c) => c.idx >= 0);
    if (yCols.length === 0) warnings.push('Pick at least one Y (value) column.');
    series = yCols.map((yc, i) => {
      const values = new Array(categories.length).fill(0);
      for (const r of rows) {
        const ci = catIndex.get(r[xi] == null ? '∅' : String(r[xi]));
        const n = toNum(r[yc.idx]);
        if (Number.isFinite(n)) values[ci] += n;
      }
      return { name: yc.name, color: colorAt(i), values };
    });
  }

  // y range across all series, with 0 as a baseline for bar/area.
  let yMin = 0, yMax = 0, any = false;
  for (const s of series) for (const v of s.values) { if (!any) { yMin = v; yMax = v; any = true; } else { yMin = Math.min(yMin, v); yMax = Math.max(yMax, v); } }
  if (!any) { yMin = 0; yMax = 1; }
  if (yMin > 0) yMin = 0;          // anchor baseline at 0 for non-negative data
  if (yMax < 0) yMax = 0;
  if (yMin === yMax) yMax = yMin + 1;
  const yTicks = niceTicks(yMin, yMax, 5);
  const tMin = yTicks[0], tMax = yTicks[yTicks.length - 1];

  return { type, categories, series, yMin: tMin, yMax: tMax, yTicks, total: 0, warnings };
}
